import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';
import { applyDefaultVariantPreference, getPreferredVariantId } from '@theme/default-variant-preference';

/** Reach per size, same values as blocks/pdp-reach-bar.liquid: [filled segments, text] */
const QUICK_BUY_REACH = {
  '1ml': [1, 'ca. 3 Tage'],
  '2ml': [2, 'ca. 1 Woche'],
  '3ml': [3, 'ca. 10 Tage'],
  '5ml': [4, 'ca. 2½ Wochen'],
  '10ml': [5, 'ca. 1 Monat'],
};

/**
 * Delegated listeners for the quick-buy layout (attached once): thumbnail
 * clicks swap the main image, size changes update the reach bar.
 * @param {HTMLElement} modalContent
 */
function setupQuickBuyInteractions(modalContent) {
  if (modalContent.dataset.qbReady) return;
  modalContent.dataset.qbReady = 'true';

  modalContent.addEventListener('click', (event) => {
    const thumb = event.target instanceof Element ? event.target.closest('.qb__thumb') : null;
    if (!thumb) return;
    const source = thumb.querySelector('img');
    const main = modalContent.querySelector('.qb__main img');
    if (!source || !main) return;
    for (const attribute of ['src', 'srcset']) {
      const value = source.getAttribute(attribute);
      if (value) main.setAttribute(attribute, value);
    }
    modalContent.querySelectorAll('.qb__thumb').forEach((button) => button.classList.toggle('is-on', button === thumb));
  });

  modalContent.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio' || !input.closest('variant-picker')) return;
    const key = /** @type {keyof typeof QUICK_BUY_REACH} */ (input.value.toLowerCase().replace(/\s+/g, ''));
    const data = QUICK_BUY_REACH[key];
    const reach = modalContent.querySelector('.pdp-reach');
    if (!data || !reach) return;
    reach.querySelectorAll('.pdp-reach__bar i').forEach((segment, index) => segment.classList.toggle('on', index < data[0]));
    const text = reach.querySelector('[data-reach-text]');
    if (text) text.textContent = data[1];
  });
}

/**
 * Quick-buy only needs the main product section, not the whole product page
 * (≈60 KB instead of ≈450 KB of HTML — the difference is very noticeable on
 * mobile data). The section id of templates/product.json is rendered into
 * #quick-add-dialog[data-product-section]; if it ever stops working (template
 * re-created), we fall back to the full page once and learn the new id from it.
 */
const QB_SECTION_STORAGE_KEY = 'qbProductSection';

function getProductSectionId() {
  try {
    const learned = sessionStorage.getItem(QB_SECTION_STORAGE_KEY);
    if (learned) return learned;
  } catch {}
  return document.getElementById('quick-add-dialog')?.dataset.productSection || '';
}

/** @param {Document} html */
function learnProductSectionId(html) {
  const section = html.querySelector('[id^="shopify-section-template--"][id$="__main"]');
  if (!section) return;
  try {
    sessionStorage.setItem(QB_SECTION_STORAGE_KEY, section.id.replace('shopify-section-', ''));
  } catch {}
}

/** @param {string} url @param {string} key @param {string} value */
function withParam(url, key, value) {
  const parsed = new URL(url, window.location.origin);
  parsed.searchParams.set(key, value);
  return parsed.pathname + parsed.search;
}

/**
 * Safety net for the mobile bottom sheet: on a real iPhone the sheet once stayed
 * invisible behind the dimmed page (not reproducible in desktop WebKit). Shortly
 * after opening we measure it; if it isn't actually on screen we force a plain,
 * animation-free bottom sheet. With ?qbdebug=1 (remembered for the tab) the
 * measurements are shown on screen so they can be screenshotted.
 * @param {HTMLElement} dialogComponent
 */
function ensureQuickBuyVisible(dialogComponent) {
  let debug = false;
  try {
    if (new URLSearchParams(location.search).has('qbdebug')) sessionStorage.setItem('qbdebug', '1');
    debug = sessionStorage.getItem('qbdebug') === '1';
  } catch {}

  const measure = () => {
    const dialog = dialogComponent.querySelector('dialog');
    if (!dialog) return null;
    const rect = dialog.getBoundingClientRect();
    const style = getComputedStyle(dialog);
    const qb = dialog.querySelector('.qb');
    const qbRect = qb?.getBoundingClientRect();
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    const visible =
      dialog.open &&
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      parseFloat(style.opacity) > 0.5 &&
      rect.height > 120 &&
      rect.top < viewportHeight - 120 &&
      rect.bottom > 120 &&
      (!qb || parseFloat(getComputedStyle(qb).opacity) > 0.5);
    return { dialog, rect, style, qbRect, viewportHeight, visible };
  };

  const report = (label, m) => {
    if (!debug || !m) return;
    let box = document.getElementById('qb-debug');
    if (!box) {
      box = document.createElement('pre');
      box.id = 'qb-debug';
      box.setAttribute('popover', 'manual');
      box.style.cssText =
        'position:fixed;inset:8px 8px auto 8px;margin:0;z-index:2147483647;max-height:45vh;overflow:auto;' +
        'background:#fff;color:#000;border:2px solid #b3261e;font:11px/1.35 monospace;padding:8px;white-space:pre-wrap;';
      box.addEventListener('click', () => box?.remove());
      document.body.appendChild(box);
    }
    const { dialog, rect, style, qbRect } = m;
    box.textContent +=
      `${label}: visible=${m.visible} open=${dialog.open} modal=${dialog.matches(':modal')}
` +
      `rect y=${Math.round(rect.top)} h=${Math.round(rect.height)} bottom=${Math.round(rect.bottom)} vh=${Math.round(m.viewportHeight)} inner=${innerHeight}
` +
      `display=${style.display} pos=${style.position} op=${style.opacity} vis=${style.visibility} tf=${style.transform} anim=${style.animationName}
` +
      `top=${style.top} bottom=${style.bottom} height=${style.height} maxH=${style.maxHeight} margin=${style.margin}
` +
      `qb: ${qbRect ? `y=${Math.round(qbRect.top)} h=${Math.round(qbRect.height)}` : 'missing'} scrollY=${Math.round(scrollY)}
` +
      `${navigator.userAgent}

`;
    try {
      box.showPopover?.();
    } catch {}
  };

  setTimeout(() => {
    const m = measure();
    report('after 450ms', m);
    if (!m || m.visible || !m.dialog.open) return;
    // Force a plain bottom sheet.
    m.dialog.style.cssText +=
      ';position:fixed!important;inset:auto 0 0 0!important;top:auto!important;bottom:0!important;margin:0!important;' +
      'width:100%!important;max-width:100%!important;height:auto!important;max-height:85vh!important;' +
      'transform:none!important;opacity:1!important;visibility:visible!important;animation:none!important;' +
      'display:flex!important;flex-direction:column!important;overflow:hidden!important;';
    m.dialog.querySelectorAll('.qb, .quick-add-modal__content').forEach((el) => {
      /** @type {HTMLElement} */ (el).style.cssText += ';opacity:1!important;animation:none!important;transform:none!important;visibility:visible!important;';
    });
    m.dialog.addEventListener('close', () => (m.dialog.style.cssText = ''), { once: true });
    setTimeout(() => report('after fallback', measure()), 300);
  }, 450);
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  /** @type {Map<string, Promise<Element | null>>} */
  #pendingContent = new Map();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();
    if (productLink?.href) return productLink.href;

    // Contexts without a <product-card> or <product-hotspot-component> ancestor
    // (e.g. the cart-drawer cross-sell row) provide the URL directly via a data
    // attribute on the component instead — falls back last so it never shadows
    // product-card's own variant-aware URL (e.g. swatch selection overrides).
    return this.dataset.productUrl || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);

    // Start loading on the first touch (≈100–300 ms before the click fires) or on hover.
    this.addEventListener('pointerdown', this.#prefetch, { passive: true });
    this.addEventListener('pointerenter', this.#prefetch, { passive: true });
  }

  #prefetch = () => {
    if (this.dataset.usesSellingPlans === 'true') return;
    const url = this.productPageUrl;
    if (url) this.#loadProductGrid(url).catch(() => {});
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    let quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';

    // A single-option card can resolve to an unavailable variant (e.g. re-selecting a
    // sold-out swatch). Keep "Choose" so shoppers reach the picker, not a dead-end disabled "Add".
    if (quickAddButton === 'add' && this.#isSelectedVariantUnavailable()) {
      quickAddButton = 'choose';
    }

    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Whether the card's currently selected swatch maps to an unavailable variant.
   * Reads `data-option-available` off the variant picker's selected option - the same
   * signal the product card uses. Only reports true on an explicit `false`, so an
   * unknown/absent signal leaves the caller's default ("add") untouched.
   * @returns {boolean}
   */
  #isSelectedVariantUnavailable() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.variantPicker?.selectedOption?.dataset.optionAvailable === 'false';
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    this.classList.add('is-loading');
    let productGrid = null;
    try {
      productGrid = await this.#loadProductGrid(currentUrl);
    } catch (error) {
      // Never leave the shopper with nothing: go to the product page instead.
      console.warn('[quick-add] could not load product', error);
      if (currentUrl) window.location.href = currentUrl;
      return;
    } finally {
      this.classList.remove('is-loading');
    }

    // null = superseded by a newer quick-add request (another card was tapped).
    if (!productGrid) return;

    // Use a fresh clone from the cache
    const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
    await this.updateQuickAddModal(freshContent);
    this.#updateVariantPicker(productGrid);

    this.#openQuickAddModal();
  };

  /**
   * Fetches (once) and caches the product grid for a product URL; concurrent
   * callers (prefetch on touch, then the click) share the same request.
   * @param {string} url
   * @returns {Promise<Element | null>}
   */
  #loadProductGrid(url) {
    const cached = this.#cachedContent.get(url);
    if (cached) return Promise.resolve(cached);
    const pending = this.#pendingContent.get(url);
    if (pending) return pending;

    const promise = (async () => {
      const html = await this.fetchProductPage(url);
      if (!html) return null; // aborted
      let gridElement = html.querySelector('[data-product-grid-content]');
      if (!gridElement) throw new Error('Product page without product grid');
      gridElement = await this.#ensureCorrectVariantSelected(gridElement, url);
      // Cache the cloned element to avoid modifying the original
      const productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
      this.#cachedContent.set(url, productGrid);
      return productGrid;
    })().finally(() => this.#pendingContent.delete(url));

    this.#pendingContent.set(url, promise);
    return promise;
  }

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
    ensureQuickBuyVisible(dialogComponent);

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Makes sure the fetched grid already reflects the variant the modal
   * should open on — the product card's own selection takes priority (a
   * shopper who picked a swatch on the card shouldn't see it overridden),
   * falling back to the 3ml/2ml default-variant business rule — *before*
   * anything is morphed into the visible modal. The theme used to fix this
   * up afterwards by flipping the radio and dispatching a change event on
   * the already-morphed-in, already-open modal — which is exactly what
   * caused the visible jump (wrong variant + price painted first, then
   * corrected a moment later). Re-fetching with `?variant=` up front means
   * only ever one, already-correct, paint.
   * @param {Element} gridElement - The freshly parsed `[data-product-grid-content]` element.
   * @param {string} productPageUrl - The URL it was fetched from.
   * @returns {Promise<Element>}
   */
  async #ensureCorrectVariantSelected(gridElement, productPageUrl) {
    try {
      if (new URL(productPageUrl, window.location.origin).searchParams.has('variant')) return gridElement;
    } catch {
      return gridElement;
    }

    const targetVariantId = this.#getSelectedVariantId() ?? getPreferredVariantId(gridElement);
    if (!targetVariantId) return gridElement;

    const currentlyChecked = gridElement.querySelector('input[type="radio"][data-variant-id]:checked');
    if (currentlyChecked?.dataset.variantId === targetVariantId) return gridElement;

    const separator = productPageUrl.includes('?') ? '&' : '?';
    const correctedHtml = await this.fetchProductPage(`${productPageUrl}${separator}variant=${targetVariantId}`);
    const correctedGrid = correctedHtml?.querySelector('[data-product-grid-content]');

    return correctedGrid ?? gridElement;
  }

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    const controller = new AbortController();
    this.#abortController = controller;

    /** @param {string} url */
    const load = async (url) => {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      return new DOMParser().parseFromString(await response.text(), 'text/html');
    };

    try {
      const sectionId = getProductSectionId();
      if (sectionId) {
        try {
          const html = await load(withParam(productPageUrl, 'section_id', sectionId));
          if (html.querySelector('[data-product-grid-content]')) return html;
        } catch (error) {
          if (error.name === 'AbortError') throw error;
        }
        try {
          sessionStorage.removeItem(QB_SECTION_STORAGE_KEY);
        } catch {}
      }

      const html = await load(productPageUrl);
      learnProductSectionId(html);
      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      if (this.#abortController === controller) this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    this.#buildQuickBuyLayout(productGrid);
    setupQuickBuyInteractions(modalContent);

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
    applyDefaultVariantPreference(modalContent);
  }

  /**
   * Rebuilds the fetched product page grid into the quick-buy layout
   * (redesign 2026-10, same look as the product page): image + thumbnails |
   * brand eyebrow, serif name, notes claim, price, sizes, reach bar, stock
   * line, buy form, link to the product page. One DOM for both breakpoints;
   * CSS (snippets/quick-buy-layout.liquid) turns it into a bottom sheet with a
   * small image on mobile. The live elements (price, variant picker, form)
   * are moved, not copied, so the theme's variant updates keep working.
   * @param {Element} productGrid - The freshly cloned product grid
   */
  #buildQuickBuyLayout(productGrid) {
    const pick = (/** @type {string} */ selector) => productGrid.querySelector(selector);
    const variantPicker = pick('variant-picker');
    const productForm = pick('product-form-component');
    if (!variantPicker && !productForm) return;

    const url = this.productPageUrl;
    const el = (/** @type {string} */ tag, /** @type {string} */ className, /** @type {string} */ html = '') => {
      const node = document.createElement(tag);
      node.className = className;
      if (html) node.innerHTML = html;
      return node;
    };

    // Images from the product gallery
    const images = Array.from(productGrid.querySelectorAll('.product-information__media slideshow-slide img'));
    const imageClone = (/** @type {Element} */ img, /** @type {string} */ sizes) => {
      const copy = /** @type {HTMLImageElement} */ (img.cloneNode(false));
      copy.removeAttribute('loading');
      copy.removeAttribute('style');
      copy.removeAttribute('class');
      copy.setAttribute('sizes', sizes);
      return copy;
    };

    const media = el('div', 'qb__media');
    if (images[0]) {
      const main = el('div', 'qb__main');
      main.appendChild(imageClone(images[0], '(min-width: 750px) 440px, 100vw'));
      media.appendChild(main);
    }
    if (images.length > 1) {
      const thumbs = el('div', 'qb__thumbs');
      images.slice(0, 6).forEach((img, index) => {
        const button = el('button', 'qb__thumb' + (index === 0 ? ' is-on' : ''));
        button.setAttribute('type', 'button');
        button.setAttribute('aria-label', `Bild ${index + 1}`);
        button.appendChild(imageClone(img, '64px'));
        thumbs.appendChild(button);
      });
      media.appendChild(thumbs);
    }

    // Heading: brand / concentration · gender, name, notes claim
    const heading = pick('.pdp-heading');
    const brand = heading?.querySelector('.pdp-heading__brand')?.textContent?.trim() || '';
    const meta = heading?.querySelector('.pdp-heading__meta')?.textContent?.trim() || '';
    const name = heading?.querySelector('.pdp-heading__title')?.textContent?.trim() || this.dataset.productTitle || '';
    // Notes chips -> "Oudh-Holz, Benzoe-Balsam & Weihrauch"
    const notes = heading ? Array.from(heading.querySelectorAll('.pdp-heading__notes li'), (li) => li.textContent.trim()) : [];
    const claim = notes.length > 1 ? `${notes.slice(0, -1).join(', ')} & ${notes[notes.length - 1]}` : notes[0] || '';

    const head = el('div', 'qb__head');
    if (images[0]) {
      const smallImage = el('a', 'qb__mimg');
      smallImage.setAttribute('href', url);
      smallImage.setAttribute('tabindex', '-1');
      smallImage.appendChild(imageClone(images[0], '88px'));
      head.appendChild(smallImage);
    }
    const headText = el('div', 'qb__htext');
    if (brand) {
      const eyebrow = el('div', 'qb__eyebrow');
      eyebrow.textContent = brand;
      if (meta) {
        const metaSpan = el('span', 'qb__meta');
        metaSpan.textContent = ` · ${meta}`;
        eyebrow.appendChild(metaSpan);
      }
      headText.appendChild(eyebrow);
    }
    const title = el('a', 'qb__title');
    title.setAttribute('href', url);
    title.textContent = name;
    headText.appendChild(title);
    if (claim) {
      const claimNode = el('p', 'qb__claim');
      claimNode.textContent = claim;
      headText.appendChild(claimNode);
    }
    const price = pick('product-price');
    if (price) headText.appendChild(price);
    head.appendChild(headText);

    const info = el('div', 'qb__info');
    info.appendChild(head);
    if (variantPicker) info.appendChild(variantPicker);
    const reach = pick('.pdp-reach-card') || pick('.pdp-reach');
    if (reach) info.appendChild(reach);
    const stock = pick('.product-stock-shipping');
    if (stock) info.appendChild(stock);

    const buy = el('div', 'qb__buy');
    if (productForm) buy.appendChild(productForm);
    const more = el(
      'div',
      'qb__more',
      '<span class="qb__more-hint">Duftnoten, Inhaltsstoffe &amp; Bewertungen</span>' +
        '<a class="qb__more-link" href=""><span class="qb__more-desk">Zur Produktseite</span>' +
        '<span class="qb__more-mob">Zur Produktseite: Duftnoten &amp; Details</span> →</a>'
    );
    more.querySelector('a')?.setAttribute('href', url);
    buy.appendChild(more);
    info.appendChild(buy);

    const layout = el('div', 'qb');
    layout.appendChild(media);
    layout.appendChild(info);
    productGrid.replaceChildren(layout);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
